import { useState } from "preact/hooks"
import useSWR from "swr"
import { SettingsLayout } from "./layout"
import { Section } from "./index"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { api_fetch, api_fetch_throws } from "src/api/client.tsx"
import { format, parseISO } from "date-fns"
import { usePageTitle } from "@/utils"
import { GuideArticle } from "@/help/page"
import { api_docs_download, api_docs_guide } from "./api_docs"

interface ApiKeyListItem {
  id: string
  name: string
  created_at: string
  revoked_at: string | null
}

interface CreateApiKeyResponse {
  id: string
  name: string
  api_key: string
  created_at: string
}

export function SettingsApiKeys() {
  usePageTitle("API Keys")
  return (
    <SettingsLayout>
      <Section
        title="API Keys"
        class="bg-white rounded-lg shadow-sm p-6"
      >
        <p class="text-sm text-gray-500 mb-4 pt-0">
          API keys let your own systems read Marginwiz data over the REST API, and connect AI assistants
          like Claude and ChatGPT via MCP. Keys are shown once at creation — store them securely.
        </p>
        <ApiKeysManager />
      </Section>
      <ApiDocsSection />
    </SettingsLayout>
  )
}

function download_text_file(filename: string, content: string) {
  const url = URL.createObjectURL(new Blob([content], { type: "text/markdown" }))
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

/** The API reference (`help/api.md`), with a download to send to whoever builds the integration. */
function ApiDocsSection() {
  const guide = api_docs_guide()
  if (!guide) return null

  function handle_download() {
    const file = api_docs_download(guide!, format(new Date(), "yyyy-MM-dd"))
    download_text_file(file.filename, file.content)
  }

  return (
    <Section
      title="API documentation"
      class="bg-white rounded-lg shadow-sm p-6 mt-6"
      action={
        <Button variant="outline" size="sm" onClick={handle_download}>
          Download docs
        </Button>
      }
    >
      <p class="text-sm text-gray-500 pt-0">
        Download a copy to send to your developer. AI agents connected over MCP read this same reference
        live with <code class="rounded bg-gray-100 px-1">get_help_guide</code> (slug{" "}
        <code class="rounded bg-gray-100 px-1">api</code>).
      </p>
      <div class="max-h-[70vh] overflow-y-auto rounded-md border border-gray-200 p-4">
        <GuideArticle slug={guide.slug} body={guide.body} />
      </div>
    </Section>
  )
}

function ApiKeysManager() {
  const { data: keys, mutate } = useSWR<ApiKeyListItem[]>("/api-keys", () =>
    api_fetch_throws<ApiKeyListItem[]>("/api-keys")
  )
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState("")
  const [newKey, setNewKey] = useState<CreateApiKeyResponse | null>(null)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState("")

  async function handle_create() {
    if (!name.trim()) return
    setError("")
    setCreating(true)
    try {
      const resp = await api_fetch<CreateApiKeyResponse>("/api-keys", {
        method: "POST",
        body: { name: name.trim() },
      })
      if (!resp.ok) {
        setError("Failed to create API key")
        return
      }
      setNewKey(resp.value.data)
      setName("")
      mutate()
    } finally {
      setCreating(false)
    }
  }

  async function handle_revoke(key_id: string) {
    const res = await api_fetch(`/api-keys/${key_id}/revoke`, { method: "POST", body: {} })
    if (res.ok) mutate()
  }

  async function handle_copy(text: string) {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const active_keys = keys?.filter((k: ApiKeyListItem) => !k.revoked_at) ?? []
  const revoked_keys = keys?.filter((k: ApiKeyListItem) => k.revoked_at) ?? []

  return (
    <div class="space-y-6">
      {/* Create new key */}
      <div class="flex items-end gap-3">
        <div class="flex-1 max-w-xs">
          <Label>Key name</Label>
          <Input
            type="text"
            placeholder="e.g. Claude MCP"
            value={name}
            onInput={(e: Event) => setName((e.target as HTMLInputElement).value)}
            onKeyDown={(e: KeyboardEvent) => e.key === "Enter" && handle_create()}
          />
        </div>
        <Button onClick={handle_create} disabled={creating || !name.trim()}>
          Generate API Key
        </Button>
      </div>
      {error && <p class="text-sm text-red-600">{error}</p>}

      {/* Newly created key banner */}
      {newKey && (
        <div class="rounded-md border border-green-200 bg-green-50 p-4">
          <p class="text-sm font-medium text-green-800 mb-2">
            API key created! Copy it now — it won't be shown again.
          </p>
          <div class="flex items-center gap-2">
            <code class="flex-1 rounded bg-white px-3 py-2 text-sm font-mono border border-green-200 select-all break-all">
              {newKey.api_key}
            </code>
            <Button variant="outline" size="sm" onClick={() => handle_copy(newKey.api_key)}>
              {copied ? "Copied!" : "Copy"}
            </Button>
          </div>
        </div>
      )}

      {/* Active keys table */}
      {active_keys.length > 0 && (
        <div>
          <h3 class="text-sm font-medium text-gray-700 mb-2">Active keys</h3>
          <table class="min-w-full divide-y divide-gray-200 text-sm">
            <thead>
              <tr class="text-left text-gray-500">
                <th class="pb-2 font-medium">Name</th>
                <th class="pb-2 font-medium">Created</th>
                <th class="pb-2 font-medium"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              {active_keys.map((key: ApiKeyListItem) => (
                <tr key={key.id}>
                  <td class="py-2 text-gray-900">{key.name}</td>
                  <td class="py-2 text-gray-500">{format(parseISO(key.created_at), "MMM d, yyyy")}</td>
                  <td class="py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      class="text-red-600 hover:text-red-700"
                      onClick={() => handle_revoke(key.id)}
                    >
                      Revoke
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Revoked keys */}
      {revoked_keys.length > 0 && (
        <div>
          <h3 class="text-sm font-medium text-gray-400 mb-2">Revoked keys</h3>
          <table class="min-w-full divide-y divide-gray-100 text-sm">
            <tbody class="divide-y divide-gray-50">
              {revoked_keys.map((key: ApiKeyListItem) => (
                <tr key={key.id} class="text-gray-400">
                  <td class="py-2 line-through">{key.name}</td>
                  <td class="py-2">{format(parseISO(key.created_at), "MMM d, yyyy")}</td>
                  <td class="py-2 text-right text-xs">
                    Revoked {format(parseISO(key.revoked_at!), "MMM d, yyyy")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {active_keys.length === 0 && !newKey && (
        <p class="text-sm text-gray-400 italic">No API keys yet. Create one above to connect AI tools to your Marginwiz data.</p>
      )}
    </div>
  )
}
